// The workspace system: the Design canvas's (4b · Machine, compact) pieces as React components. Styles in ds.css,
// values in tokens.css layer 5. Icons are Tabler at stroke 1.5 beside regular text.

import { useEffect, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ComponentType, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { isMac } from '../keys';
import { REDUCED_MS, reducedMotion } from '../motion';
import './ds.css';

export type TablerIcon = ComponentType<{ size?: number; stroke?: number; className?: string; 'aria-hidden'?: boolean }>;

/* ---------- text ---------- */

export const Pix = ({ children, size = 10, className = '' }: { children: ReactNode; size?: 10 | 11; className?: string }) => (
  <span className={`ds-pix ${className}`} data-size={size === 11 ? '11' : undefined}>
    {children}
  </span>
);

export const Mono = ({ children, size = 11, className = '' }: { children: ReactNode; size?: 11 | 12; className?: string }) => (
  <span className={`ds-mono ${className}`} data-size={size === 12 ? '12' : undefined}>
    {children}
  </span>
);

export const Meta = ({ children, size = 12, className = '' }: { children: ReactNode; size?: 11 | 12; className?: string }) => (
  <span className={`ds-meta ${className}`} data-size={size === 11 ? '11' : undefined}>
    {children}
  </span>
);

/* ---------- icons ---------- */

export const Ic = ({ icon: I, size = 16, stroke = 1.5 }: { icon: TablerIcon; size?: number; stroke?: number }) => (
  <I size={size} stroke={stroke} className="ds-ic" aria-hidden />
);

/** An icon that changes with a state (Copy → Copied): both stay in the DOM and cross-fade, the one coming in
 *  growing from 25% out of a 4px blur, the one going the reverse (.ds-swap in ds.css). */
export const IconSwap = ({ on, off: Off, to: On, size = 16, stroke = 1.5, onStroke = stroke }: { on: boolean; off: TablerIcon; to: TablerIcon; size?: number; stroke?: number; onStroke?: number }) => (
  <span className="ds-swap" data-on={on ? '' : undefined} aria-hidden="true">
    <Off size={size} stroke={stroke} className="ds-ic" />
    <On size={size} stroke={onStroke} className="ds-ic" />
  </span>
);

/** The Mac's modifier marks, drawn and never typed: the mono face has no ⌃ ⌥ ⇧ ⌘, so a typed one came from
    whatever font the system fell back to (thin, off the line, an emoji's on some). Tabler's chevron-up (raised,
    as ⌃ sits), option, arrow-big-up and command, each cut to its own drawing so nothing empty stands beside
    it, 8px tall like the key's capital and on a stroke of about 1px like the letter's. */
const MOD_MARKS: Record<string, { name: string; w: number; box: string; stroke: number; d: string }> = {
  '⌃': { name: 'Control', w: 8, box: '5 4 14 14', stroke: 1.75, d: 'M6 12l6 -6l6 6' },
  '⌥': { name: 'Option', w: 9, box: '4 5 16 14', stroke: 1.75, d: 'M14 6h5m0 12h-5l-5 -12h-4' },
  '⇧': { name: 'Shift', w: 7, box: '3 2 18 20', stroke: 2.5, d: 'M9 20v-8h-3.586a1 1 0 0 1 -.707 -1.707l6.586 -6.586a1 1 0 0 1 1.414 0l6.586 6.586a1 1 0 0 1 -.707 1.707h-3.586v8a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1' },
  '⌘': { name: 'Command', w: 8, box: '4 4 16 16', stroke: 2, d: 'M7 9a2 2 0 1 1 2 -2v10a2 2 0 1 1 -2 -2h10a2 2 0 1 1 -2 2v-10a2 2 0 1 1 2 2h-10' },
};
const ModMark = ({ g }: { g: string }) => {
  const m = MOD_MARKS[g];
  return (
    <svg className="ds-ic" width={m.w} height="8" viewBox={m.box} fill="none" stroke="currentColor" strokeWidth={m.stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={m.d} />
    </svg>
  );
};
/** ⌘ in a hint (⌘K), at 12 so it stands as tall as the key's capital beside it, its stroke 1px like the letter's. */
const Cmd = () => (
  <svg className="ds-ic" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={MOD_MARKS['⌘'].d} />
  </svg>
);

/** A shortcut: ⌘ and the key on a Mac, Ctrl+key elsewhere. `mod={false}` for a bare key. */
function Keys({ k, mod = true }: { k: string; mod?: boolean }) {
  if (!mod) return <span>{k}</span>;
  if (!isMac()) return <span>Ctrl+{k}</span>;
  return (
    <>
      <span className="ds-sr">Command </span>
      <Cmd />
      <span>{k}</span>
    </>
  );
}

export const Hint = ({ k, mod, tone }: { k: string; mod?: boolean; tone?: 'muted' }) => (
  <span className="ds-hint" data-tone={tone}>
    <Keys k={k} mod={mod} />
  </span>
);

export const Kbd = ({ k, mod, onSurface }: { k: string; mod?: boolean; onSurface?: boolean }) => (
  <kbd className="ds-kbd" data-on={onSurface ? 'surface' : undefined}>
    <Keys k={k} mod={mod} />
  </kbd>
);

/** A key's cap from how it's printed ("⌥ Option", "⌥", "Space", "Ctrl"): a Mac modifier's mark is drawn, the
    rest is type. A mark standing alone is read out by its name. */
export function KeyCap({ cap, onSurface }: { cap: string; onSurface?: boolean }) {
  const mark = MOD_MARKS[cap[0]];
  const word = mark ? cap.slice(1).trim() : cap;
  return (
    <kbd className="ds-kbd" data-on={onSurface ? 'surface' : undefined}>
      {mark && <ModMark g={cap[0]} />}
      {mark && !word && <span className="ds-sr">{mark.name}</span>}
      {word && <span className={mark ? 'ds-kbd-word' : undefined}>{word}</span>}
    </kbd>
  );
}

/* ---------- buttons ---------- */

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'icon';
  size?: 'md' | 'sm' | 'xs';
  tone?: 'danger';
  /** icon buttons in bars and the sidebar use 6px corners */
  radius?: 6 | 8;
};

export function Button({ variant = 'secondary', size = 'md', tone, radius, className = '', type = 'button', ...rest }: BtnProps) {
  return (
    <button
      type={type}
      className={`ds-btn ${className}`}
      data-variant={variant}
      data-size={size === 'md' ? undefined : size}
      data-tone={tone}
      data-radius={radius === 6 ? '6' : undefined}
      {...rest}
    />
  );
}

/** Nudges a tooltip sideways so it stays 8px inside whatever would cut it off (a sidebar, the window). */
function keepInside(tt: HTMLElement | null) {
  if (!tt) return;
  tt.style.marginLeft = '';
  let clip: Element | null = tt.parentElement?.parentElement ?? null;
  while (clip && clip !== document.body && getComputedStyle(clip).overflowX === 'visible') clip = clip.parentElement;
  const box = clip && clip !== document.body ? clip.getBoundingClientRect() : { left: 0, right: document.documentElement.clientWidth };
  const at = tt.getBoundingClientRect();
  const nudge = Math.max(box.left + 8 - at.left, 0) + Math.min(box.right - 8 - at.right, 0);
  if (nudge) tt.style.marginLeft = `${nudge}px`;
}

/* Tooltips share one clock (Radix and Base UI's rule): the first waits 400ms (ds.css), so passing over
   a row of controls doesn't set one off; while one is up, and for 300ms after it goes, the next opens at
   once with no animation (`data-instant`). */
const TIP_DELAY = 400;
const TIP_WARM = 300;
let tipsUp = 0;
let tipWarmUntil = 0;

function useTipClock(tt: RefObject<HTMLSpanElement | null>) {
  const shown = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const enter = () => {
    const el = tt.current;
    if (!el) return;
    keepInside(el);
    window.clearTimeout(timer.current);
    const instant = tipsUp > 0 || performance.now() < tipWarmUntil;
    el.toggleAttribute('data-instant', instant);
    const up = () => {
      if (shown.current) return;
      shown.current = true;
      tipsUp++;
    };
    if (instant) up();
    else timer.current = window.setTimeout(up, TIP_DELAY);
  };
  const leave = () => {
    window.clearTimeout(timer.current);
    tt.current?.parentElement?.removeAttribute('data-quiet');
    if (!shown.current) return;
    shown.current = false;
    tipsUp = Math.max(0, tipsUp - 1);
    tipWarmUntil = performance.now() + TIP_WARM;
  };
  /** A press puts it away until the pointer leaves and comes back. */
  const press = () => {
    leave();
    tt.current?.parentElement?.setAttribute('data-quiet', '');
  };
  useEffect(() => () => leave(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return { enter, leave, press };
}

/** A tooltip under its trigger: the name, an optional line, an optional shortcut. */
export function Tooltip({ label, sub, k, align, children }: { label: string; sub?: string; k?: string; align?: 'end'; children: ReactNode }) {
  const tt = useRef<HTMLSpanElement>(null);
  const clock = useTipClock(tt);
  return (
    <span
      className="ds-tw"
      onPointerEnter={clock.enter}
      onPointerLeave={clock.leave}
      onPointerDown={clock.press}
      onFocus={clock.enter}
      onBlur={clock.leave}
    >
      {children}
      <span className="ds-tt" ref={tt} role="tooltip" data-align={align}>
        <span className="ds-tt-label">{label}</span>
        {sub && <Meta>{sub}</Meta>}
        {k && <Hint k={k} tone="muted" />}
      </span>
    </span>
  );
}

/** An icon button that says what it is on hover. `held`: while ⌘ is held (.kbd-held on the root, the
    sidebar's useKbdHeld) its shortcut shows as a cap right under the icon, as the Mac's SidebarTool does. */
export function Tool({
  icon,
  label,
  sub,
  k,
  held,
  size = 'sm',
  iconSize = 16,
  radius = 6,
  align,
  pressed,
  tone,
  children,
  ...rest
}: Omit<BtnProps, 'variant' | 'children'> & { icon: TablerIcon; label: string; sub?: string; k?: string; held?: boolean; iconSize?: number; align?: 'end'; pressed?: boolean; children?: ReactNode }) {
  return (
    <Tooltip label={label} sub={sub} k={k} align={align}>
      <Button variant="icon" size={size} radius={radius} tone={tone} aria-label={label} aria-pressed={pressed === undefined ? undefined : pressed} style={{ position: 'relative' }} {...rest}>
        <Ic icon={icon} size={iconSize} />
        {children}
      </Button>
      {held && k && (
        <span className="ds-tool-cap" aria-hidden="true" style={{ '--icon': `${iconSize}px` } as CSSProperties}>
          <Kbd k={k} onSurface />
        </span>
      )}
    </Tooltip>
  );
}

/* ---------- segmented control ---------- */

export type SegItem<V extends string> = { value: V; label?: string; icon?: TablerIcon; glyph?: ReactNode; aria?: string; suffix?: ReactNode; testid?: string };

/** The one pill-tab control (Light / Dark / System, a time range, Activity's tabs): a radio group, or with `tabs`
    a tablist whose items point at the panel the parent owns (`controls`) and carry ids (`${idPrefix}-${value}`). */

export function Segmented<V extends string>({ items, value, onChange, label, tabs }: { items: SegItem<V>[]; value: V; onChange: (v: V) => void; label: string; tabs?: { controls?: string; idPrefix?: string } }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ x: number; w: number } | null>(null);
  const [glide, setGlide] = useState(false);

  useLayoutEffect(() => {
    const track = ref.current;
    if (!track) return;
    const measure = () => {
      const el = track.querySelector<HTMLElement>('[data-on]');
      if (el) {
        // fractional, like the item itself, so the pill's edges land where the label's do
        const a = el.getBoundingClientRect();
        const x = a.left - track.getBoundingClientRect().left;
        setPill((p) => (p && p.x === x && p.w === a.width ? p : { x, w: a.width }));
      }
    };
    measure();
    // labels change width when the webfont lands (and on zoom): follow them
    const ro = new ResizeObserver(measure);
    track.querySelectorAll('.ds-seg-item').forEach((el) => ro.observe(el));
    return () => ro.disconnect();
  }, [value, items.length]);
  // the pill appears in place, then glides on later changes
  useEffect(() => {
    if (pill && !glide) requestAnimationFrame(() => setGlide(true));
  }, [pill, glide]);

  const move = (dir: 1 | -1) => {
    const i = items.findIndex((it) => it.value === value);
    const next = items[(i + dir + items.length) % items.length];
    onChange(next.value);
    requestAnimationFrame(() => ref.current?.querySelector<HTMLElement>('[data-on]')?.focus());
  };

  return (
    <div ref={ref} className="ds-seg" role={tabs ? 'tablist' : 'radiogroup'} aria-label={label} data-measured={pill ? '' : undefined}>
      {pill && (
        <span
          className="ds-seg-pill"
          aria-hidden="true"
          style={{ width: pill.w, transform: `translateX(${pill.x}px)`, transition: glide ? undefined : 'none' }}
        />
      )}
      {items.map((it) => {
        const on = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role={tabs ? 'tab' : 'radio'}
            aria-checked={tabs ? undefined : on}
            aria-selected={tabs ? on : undefined}
            aria-controls={tabs?.controls}
            id={tabs?.idPrefix ? `${tabs.idPrefix}-${it.value}` : undefined}
            data-on={on ? '' : undefined}
            data-testid={it.testid}
            aria-label={it.label ? undefined : it.aria}
            tabIndex={on ? 0 : -1}
            className="ds-seg-item"
            data-glyph={it.icon || it.glyph ? (it.label ? '' : undefined) : undefined}
            data-icon-only={!it.label ? '' : undefined}
            onClick={() => onChange(it.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') (e.preventDefault(), move(1));
              if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') (e.preventDefault(), move(-1));
            }}
          >
            {it.icon && <Ic icon={it.icon} size={14} />}
            {it.glyph}
            {it.label}
            {it.suffix}
          </button>
        );
      })}
    </div>
  );
}

/** A terminal cursor drawn in a 14px box, for the Cursor setting: block, bar or underline. */
export const CursorGlyph = ({ shape }: { shape: 'block' | 'bar' | 'underline' }) => {
  const [x, y, w, h] = { block: [3, 1, 8, 12], bar: [6, 1, 2, 12], underline: [3, 11, 8, 2] }[shape];
  return (
    <svg className="ds-ic" width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
      <rect x={x} y={y} width={w} height={h} rx="0.5" fill="currentColor" />
    </svg>
  );
};

/* ---------- switch, radio, stepper ---------- */

export const Switch = ({ on, onChange, label, disabled }: { on: boolean; onChange: (on: boolean) => void; label: string; disabled?: boolean }) => (
  <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className="ds-switch" onClick={() => onChange(!on)} />
);

export const RadioMark = ({ on }: { on: boolean }) => <span className="ds-radio" data-on={on ? '' : undefined} aria-hidden="true" />;

export type CheckState = 'on' | 'off' | 'mixed' | 'locked' | 'unavailable';

/** A checkbox (the Mac's DSCheckbox): a 16px box in a 24px target. On and mixed fill with ink; locked (the cloud
 *  machine has it already) is a faint tick on the canvas; unavailable (it can't go) an empty well. A real
 *  checkbox under it keeps the keys and the screen reader; wrap it in a <label> with the row's words. */
export function Checkbox({ state, onChange, label, testid }: { state: CheckState; onChange: (on: boolean) => void; label?: string; testid?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'mixed';
  }, [state]);
  return (
    <span className="ds-check" data-state={state}>
      <input
        ref={ref}
        type="checkbox"
        checked={state === 'on' || state === 'locked'}
        disabled={state === 'locked' || state === 'unavailable'}
        aria-label={label}
        onChange={() => onChange(state !== 'on')}
        onClick={(e) => e.stopPropagation()}
        data-testid={testid}
      />
      <span className="ds-check-box" aria-hidden="true">
        <svg viewBox="0 0 12 12">
          <path className="ds-check-tick" d="M2.5 6.2l2.3 2.3 4.7-5" />
          <path className="ds-check-dash" d="M3 6h6" />
        </svg>
      </span>
    </span>
  );
}

export function Stepper({ value, onDec, onInc, label, decLabel = 'Smaller', incLabel = 'Larger', decIcon, incIcon }: { value: ReactNode; onDec: () => void; onInc: () => void; label: string; decLabel?: string; incLabel?: string; decIcon: TablerIcon; incIcon: TablerIcon }) {
  return (
    <div className="ds-stepper" role="group" aria-label={label}>
      <Button variant="icon" aria-label={decLabel} onClick={onDec}>
        <Ic icon={decIcon} size={14} />
      </Button>
      <span className="ds-stepper-value" aria-live="polite">
        {value}
      </span>
      <Button variant="icon" aria-label={incLabel} onClick={onInc}>
        <Ic icon={incIcon} size={14} />
      </Button>
    </div>
  );
}

/* ---------- status mark ---------- */

export type MarkTone = 'run' | 'live' | 'attn' | 'idle' | 'fault';
const MARK: Record<MarkTone, { fill: string; ring?: string }> = {
  run: { fill: 'var(--accent)', ring: 'var(--run-ring)' },
  live: { fill: 'var(--live)', ring: 'var(--live-ring)' },
  attn: { fill: 'var(--attn)', ring: 'var(--attn-ring)' },
  idle: { fill: 'var(--idle)' },
  fault: { fill: 'var(--fault)' },
};

/** A 6px square; `ring` adds the soft 12px halo (what needs you), `blink` flashes it (what's working). */
export function Mark({ tone, ring, blink }: { tone: MarkTone; ring?: boolean; blink?: boolean }) {
  const m = MARK[tone];
  return (
    <svg className="ds-mark" width="6" height="6" viewBox="0 0 6 6" aria-hidden="true" data-blink={blink ? '' : undefined}>
      {ring && m.ring && <rect x="-3" y="-3" width="12" height="12" rx="3" fill={m.ring} />}
      <rect width="6" height="6" rx="1" fill={m.fill} />
    </svg>
  );
}

/* ---------- the machine ---------- */

/** The little aluminium machine; its light in a status's colour (live by default), or none when it's off. */
export const Mini = ({ on = true, tone }: { on?: boolean; tone?: 'run' | 'attn' | 'fault' }) => (
  <span className="ds-mini" data-off={on ? undefined : ''} data-tone={tone} aria-hidden="true" />
);

/* the landing's caret row (^^^), drawn inline: the workspace doesn't load the landing's sprites */
const CARET = 'M1.368 0.228V0H2.736V0.228H1.368ZM1.368 0.684V0.456H2.736V0.684H1.368ZM2.28 1.14V0.912H3.192V1.14H2.28ZM0.912 1.14V0.912H1.824V1.14H0.912ZM0.912 1.596V1.368H1.824V1.596H0.912ZM2.28 1.596V1.368H3.192V1.596H2.28ZM0.456 2.052V1.824H1.368V2.052H0.456ZM2.736 2.052V1.824H3.648V2.052H2.736ZM2.736 2.508V2.28H3.648V2.508H2.736ZM0.456 2.508V2.28H1.368V2.508H0.456ZM3.192 2.964V2.736H4.104V2.964H3.192Z';
export const Carets = ({ className = 'empty-carets' }: { className?: string }) => (
  <svg className={className} width="30" height="5" viewBox="0 0 24.1665 3.42" aria-hidden="true">
    {[0, 5.0156, 10.0312, 15.0469, 20.0625].map((x) => (
      <path key={x} fill="currentColor" transform={`translate(${x} 0)`} d={CARET} />
    ))}
  </svg>
);

/* ---------- presence: mount, enter, leave, unmount ---------- */

/** For a floating surface: `mounted` while open or leaving, `shown` once it should be at rest, `leaving` on
 *  its way out. Spread presence(p) on a .ds-float: it enters from its start state and leaves to a smaller,
 *  nearer one, both with CSS transitions, so either can be interrupted by the other. */
export function usePresence(open: boolean, exitMs = 150) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(open);
  const live = useRef(open);
  live.current = mounted;
  useEffect(() => {
    if (open) {
      // reopened while leaving: straight back to rest from where it is
      if (live.current) return setShown(true);
      setMounted(true);
      // one frame at the start state so the entrance runs
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), reducedMotion() ? REDUCED_MS : exitMs);
    return () => clearTimeout(t);
  }, [open, exitMs]);
  return { mounted, shown, leaving: mounted && !open };
}

/** The attributes a .ds-float takes from usePresence. */
export const presence = (p: { shown: boolean; leaving: boolean }) => ({
  'data-shown': p.shown ? '' : undefined,
  'data-leaving': p.leaving ? '' : undefined,
});

/** For a list of surfaces (a toast stack): an item taken out of `items` stays `exitMs` longer, where it
 *  was, with `leaving` set, so it can play its exit (data-leaving on the card). */
export function useLeaving<T>(items: T[], key: (t: T) => string, exitMs = 150): { item: T; leaving: boolean }[] {
  const gone = useRef(new Map<string, { item: T; index: number; at: number }>());
  const prev = useRef(items);
  const [, redraw] = useState(0);
  const now = new Set(items.map(key));
  if (prev.current !== items) {
    const at = performance.now();
    prev.current.forEach((t, index) => {
      const k = key(t);
      if (!now.has(k) && !gone.current.has(k)) gone.current.set(k, { item: t, index, at });
    });
    for (const k of now) gone.current.delete(k); // back before it finished leaving
    prev.current = items;
  }
  useEffect(() => {
    if (!gone.current.size) return;
    const first = Math.min(...[...gone.current.values()].map((g) => g.at));
    const t = setTimeout(() => {
      const end = performance.now() - exitMs;
      for (const [k, g] of gone.current) if (g.at <= end + 1) gone.current.delete(k);
      redraw((n) => n + 1);
    }, Math.max(0, first + exitMs - performance.now()));
    return () => clearTimeout(t);
  });
  const out = items.map((item) => ({ item, leaving: false }));
  for (const g of [...gone.current.values()].sort((a, b) => a.index - b.index)) out.splice(Math.min(g.index, out.length), 0, { item: g.item, leaving: true });
  return out;
}

export { Empty, EmptyArt, type EmptyArtKind, type EmptySize } from './Empty';
